import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IMAGE_ACCEPT, isImage } from "@/lib/upload";
import { Camera, Link2, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const btn = "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60";

export function PropertyPhotoField({ imageUrl, onUrlChange, file, onFileChange, uploaded, onRemoveUploaded, progress }) {
  const ref = useRef(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const u = URL.createObjectURL(file); setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const pick = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!isImage(f) || f.size > 10 * 1024 * 1024) return toast.error("Only JPG, PNG or WEBP photos under 10 MB");
    onFileChange(f);
  };
  const shown = preview || imageUrl;

  return (
    <div className="space-y-2" data-testid="prop-photo-field">
      <Label>Property photo (optional)</Label>
      <div className="flex gap-3 items-start">
        <div className="h-20 w-28 shrink-0 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-300">
          {shown ? <img src={shown} alt="" className="h-full w-full object-cover" data-testid="prop-photo-preview" /> : <Camera className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => ref.current?.click()} disabled={progress !== null} data-testid="prop-photo-upload"
              className={`${btn} border-primary/40 text-primary hover:bg-emerald-50`}>
              {progress !== null ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading {progress}%</> : <><Camera className="h-3.5 w-3.5" /> {file ? "Change photo" : "Upload a photo"}</>}
            </button>
            {file && <button type="button" onClick={() => onFileChange(null)} className={`${btn} border-slate-200 text-slate-600 hover:bg-slate-50`} data-testid="prop-photo-clear"><X className="h-3.5 w-3.5" /> Clear</button>}
            {uploaded && !file && <button type="button" onClick={onRemoveUploaded} className={`${btn} border-rose-200 text-rose-600 hover:bg-rose-50`} data-testid="prop-photo-remove"><Trash2 className="h-3.5 w-3.5" /> Remove uploaded photo</button>}
          </div>
          {file && <p className="text-[11px] text-slate-500 truncate" data-testid="prop-photo-filename">{file.name} · uploads when you save</p>}
          <div className="relative">
            <Link2 className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input value={uploaded && !file ? "" : imageUrl} onChange={(e) => onUrlChange(e.target.value)} className="pl-8 h-9 text-xs" data-testid="prop-image-url"
              placeholder={uploaded ? "Using your uploaded photo — or paste a URL to replace it" : "…or paste a photo URL (https://…)"} />
          </div>
        </div>
      </div>
      <input ref={ref} type="file" accept={IMAGE_ACCEPT} className="hidden" onChange={pick} data-testid="prop-photo-input" />
    </div>
  );
}
